import { useState, useEffect, useCallback } from 'react';
import API from '../api/axios';
import { Icon } from '../components/SvgIcons';
import BulkUploadModal from '../components/BulkUploadModal';
import { getTenantUrl } from '../utils/tenant';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  university_admin: 'University Admin',
  college_admin: 'College Admin',
  hod: 'HOD',
  moderator: 'Moderator',
  examiner: 'Examiner',
  subject_matter_expert: 'SME',
  coe: 'COE'
};

const ROLE_BADGE = (role) => {
  const map = {
    university_admin: { bg: 'var(--info-light)', color: 'var(--info)' },
    college_admin: { bg: 'var(--primary-light)', color: 'var(--primary)' },
    coe: { bg: '#fdf4ff', color: '#7e22ce' },
    hod: { bg: '#fff7ed', color: '#c2410c' },
    moderator: { bg: '#f3e8ff', color: '#6b21a8' },
    subject_matter_expert: { bg: '#ecfdf5', color: '#065f46' },
    examiner: { bg: '#eff6ff', color: '#1d4ed8' },
  };
  return map[role] || { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };
};

const STATUS_BADGE = (status) => {
  const map = {
    active: { bg: 'var(--success-light)', color: 'var(--success)' },
    inactive: { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)' },
    pending: { bg: 'var(--warning-light)', color: 'var(--warning)' },
    suspended: { bg: 'var(--danger-light)', color: 'var(--danger)' },
  };
  return map[status] || { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };
};

const roleNeedsCollege = (role) =>
  ['college_admin', 'hod', 'moderator', 'subject_matter_expert', 'examiner'].includes(role);
const roleNeedsDepartment = (role) =>
  ['hod', 'moderator', 'subject_matter_expert', 'examiner'].includes(role);
// Subject assignment removed from user creation - will be assigned after user creation

const subjectLabel = (s) => `${s.subject_name}${s.subject_code ? ` (${s.subject_code})` : ''}`;

function SubjectsCell({ subjects }) {
  const [popoverPos, setPopoverPos] = useState(null);

  if (!subjects || subjects.length === 0) return null;

  if (subjects.length === 1) {
    const [s] = subjects;
    return (
      <span style={{ color: 'var(--text-tertiary)', fontSize: '0.72rem' }}>
        {s.subject_name}
        {s.subject_code && (
          <span style={{ fontWeight: 700, color: 'var(--info)' }}> ({s.subject_code})</span>
        )}
      </span>
    );
  }

  const showPopover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopoverPos({ top: rect.bottom + 6, left: rect.left });
  };

  return (
    <span>
      <span
        onMouseEnter={showPopover}
        onMouseLeave={() => setPopoverPos(null)}
        style={{
          fontSize: '0.72rem', fontWeight: 700, padding: '0.1rem 0.45rem', borderRadius: '999px',
          backgroundColor: 'var(--info-light)', color: 'var(--info)', cursor: 'default',
          display: 'inline-block'
        }}
      >
        {subjects.length} subjects
      </span>
      {popoverPos && (
        <div style={{
          position: 'fixed', top: popoverPos.top, left: popoverPos.left, zIndex: 1000,
          background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
          borderRadius: '8px', boxShadow: 'var(--shadow-xl)', padding: '0.5rem 0.75rem',
          maxWidth: '320px', pointerEvents: 'none'
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            Assigned Subjects
          </div>
          {subjects.map(s => (
            <div key={s.subject_id} style={{ fontSize: '0.78rem', color: 'var(--text-primary)', padding: '0.1rem 0' }}>
              {subjectLabel(s)}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

function formatElapsed(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function Pagination({ page, totalPages, total, pageSize, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.75rem 0', borderTop: '1px solid var(--border-color)', marginTop: '0.5rem'
    }}>
      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
        {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total} users
      </span>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <button disabled={page === 1} onClick={() => onChange(page - 1)} className="btn btn-secondary btn-sm">Previous</button>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', padding: '0 0.25rem' }}>{page} / {totalPages}</span>
        <button disabled={page === totalPages} onClick={() => onChange(page + 1)} className="btn btn-secondary btn-sm">Next</button>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)'
    }}>
      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', minWidth: '120px', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)', textAlign: 'right', wordBreak: 'break-word', flex: 1 }}>{value || '—'}</span>
    </div>
  );
}

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
function SuperAdminUserManagement() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantSlug, setSelectedTenantSlug] = useState('');
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [collegeFilter, setCollegeFilter] = useState('');
  const [colleges, setColleges] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [confirmImpersonateId, setConfirmImpersonateId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [impersonateError, setImpersonateError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('users');
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const PAGE_SIZE = 15;

  const selectedTenant = tenants.find(t => t.slug === selectedTenantSlug);
  const isUniversityTenant = selectedTenant?.tenant_type === 'university';
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const allSelected = users.length > 0 && users.every(u => selected.has(u.id));

  useEffect(() => {
    API.get('/superadmin/tenants')
      .then(({ data }) => { if (data.success) setTenants(data.tenants || []); })
      .catch(() => {})
      .finally(() => setLoadingTenants(false));
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!selectedTenantSlug) return;
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      if (collegeFilter) params.college_id = collegeFilter;
      const { data } = await API.get(`/tenants/${selectedTenantSlug}/users`, { params });
      if (data.success) {
        setUsers(data.users);
        setTotal(data.total);
        if (data.stats) setStats(data.stats);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantSlug, page, search, roleFilter, statusFilter, collegeFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { setPage(1); setSelected(new Set()); }, [search, roleFilter, statusFilter, collegeFilter, selectedTenantSlug]);

  // Load colleges for the college filter — only relevant for university tenants.
  useEffect(() => {
    setCollegeFilter('');
    setColleges([]);
    if (!selectedTenantSlug || !isUniversityTenant) return;
    API.get(`/tenants/${selectedTenantSlug}/colleges`)
      .then(({ data }) => { if (data.success) setColleges(data.colleges || []); })
      .catch(() => setColleges([]));
  }, [selectedTenantSlug, isUniversityTenant]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleToggleStatus = async (user) => {
    setTogglingId(user.id);
    try {
      await API.put(`/tenants/${selectedTenantSlug}/users/${user.id}/status`, { is_active: !user.is_active });
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, is_active: !u.is_active, status: u.is_active ? 'inactive' : 'active' } : u
      ));
      showToast(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update status', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    const ids = Array.from(selected);
    try {
      await Promise.all(
        ids.map(id => API.put(`/tenants/${selectedTenantSlug}/users/${id}/status`, {
          is_active: action === 'activate'
        }))
      );
      showToast(`${ids.length} user${ids.length > 1 ? 's' : ''} ${action === 'activate' ? 'activated' : 'deactivated'}`);
      setSelected(new Set());
      fetchUsers();
    } catch {
      showToast('Bulk action partially failed — refresh to check status', 'error');
      fetchUsers();
    } finally {
      setBulkLoading(false);
    }
  };

  const handleImpersonateClick = (userId) => {
    if (confirmImpersonateId === userId) {
      handleImpersonateConfirm(userId);
    } else {
      setConfirmImpersonateId(userId);
      setImpersonateError(null);
      setTimeout(() => setConfirmImpersonateId(null), 3000);
    }
  };

  const handleImpersonateConfirm = async (userId) => {
    setImpersonatingId(userId);
    setConfirmImpersonateId(null);
    setImpersonateError(null);
    try {
      const { data } = await API.post(`/tenants/${selectedTenantSlug}/users/${userId}/impersonate`);
      if (data.success) {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (token) localStorage.setItem('admin_access_token_backup', token);
        if (userData) localStorage.setItem('admin_user_backup', userData);
        localStorage.setItem('is_impersonating', 'true');
        if (data.user) localStorage.setItem('impersonation_target', JSON.stringify(data.user));
        const targetUrl = getTenantUrl(selectedTenantSlug);
        const params = new URLSearchParams({ access_token: data.token, impersonating: 'true' });
        window.open(`${targetUrl}#${params.toString()}`, '_blank', 'noopener');
        setImpersonatingId(null);
      }
    } catch (err) {
      setImpersonateError(err.response?.data?.message || 'Impersonation failed');
      setImpersonatingId(null);
    }
  };

  const handleExport = () => {
    if (users.length === 0) return;
    const headers = ['ID', 'Name', 'Email', 'Role', 'College', 'Department', 'Dept Code', 'Subject', 'Subject Code', 'Status', 'Phone', 'Joined', 'Last Login'];
    const escape = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
    const rows = users.map(u => [
      u.id,
      escape(u.name),
      u.email,
      u.role,
      escape(u.college_name || ''),
      escape(u.department_name || ''),
      u.department_code || '',
      escape((u.subjects || []).map(s => s.subject_name).join('; ')),
      escape((u.subjects || []).map(s => s.subject_code || '').join('; ')),
      u.status,
      u.phone || '',
      u.created_at ? new Date(u.created_at).toLocaleDateString() : '',
      u.last_login ? new Date(u.last_login).toLocaleDateString() : ''
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${selectedTenantSlug}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(users.map(u => u.id)));
    }
  };

  const handleSelectOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">User Management</h1>
        <p className="dashboard-subtitle" style={{ color: 'white' }}>
          Create and manage users across tenants — no approval required
        </p>
      </div>

      <div className="card">
        {/* ── Tenant Selector ─────────────────────────────────── */}
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Tenant:</label>
            {loadingTenants ? (
              <span style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>Loading tenants...</span>
            ) : (
              <select
                value={selectedTenantSlug}
                onChange={(e) => setSelectedTenantSlug(e.target.value)}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)', fontSize: '0.875rem',
                  fontWeight: 500, minWidth: '280px', cursor: 'pointer',
                  backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)'
                }}
              >
                <option value="">— Select a tenant —</option>
                {tenants.map(t => (
                  <option key={t.slug} value={t.slug}>{t.university_name} ({t.slug})</option>
                ))}
              </select>
            )}
            {selectedTenant && (
              <>
                <span style={{
                  fontSize: '0.75rem', fontWeight: 700, padding: '0.2rem 0.6rem',
                  borderRadius: '4px', textTransform: 'capitalize',
                  backgroundColor: selectedTenant.tenant_type === 'university' ? 'var(--success-light)' : 'var(--warning-light)',
                  color: selectedTenant.tenant_type === 'university' ? 'var(--success)' : 'var(--warning)'
                }}>
                  {selectedTenant.tenant_type}
                </span>
                {stats && (
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {stats.total} total · {stats.active} active · {stats.pending} pending
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {!selectedTenantSlug ? (
          <div style={{ textAlign: 'center', padding: '5rem 1rem' }}>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center', color: 'var(--text-tertiary)' }}><Icon name="Users" size={44} /></div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
              Select a tenant to manage users
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Users created here are immediately active — no approval workflow required
            </div>
          </div>
        ) : (
          <>
            {/* ── Stats Bar ───────────────────────────────────── */}
            {stats && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-secondary)'
              }}>
                {[
                  { label: 'Total Users', value: stats.total, color: 'var(--text-primary)' },
                  { label: 'Active', value: stats.active, color: 'var(--success)' },
                  { label: 'Inactive', value: stats.inactive, color: 'var(--text-secondary)' },
                  { label: 'Pending', value: stats.pending, color: 'var(--warning)' },
                ].map((s, i) => (
                  <div key={i} style={{
                    padding: '0.875rem 1rem', textAlign: 'center',
                    borderRight: i < 3 ? '1px solid var(--border-color)' : 'none'
                  }}>
                    <div style={{ fontSize: '1.35rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Main Tabs ───────────────────────────────────── */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', padding: '0 1.5rem', backgroundColor: 'var(--bg-secondary)' }}>
              {[{ key: 'users', label: 'Users' }, { key: 'logs', label: 'Upload Logs' }].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveMainTab(tab.key)}
                  style={{
                    padding: '0.7rem 1.2rem', background: 'none', border: 'none',
                    borderBottom: activeMainTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
                    color: activeMainTab === tab.key ? 'var(--primary)' : 'var(--text-secondary)',
                    fontWeight: activeMainTab === tab.key ? 700 : 500,
                    fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.15s',
                    marginBottom: '-1px'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeMainTab === 'users' ? (<>
            {/* ── Toolbar ─────────────────────────────────────── */}
            <div style={{
              padding: '0.75rem 1.5rem', borderBottom: '1px solid var(--border-color)',
              display: 'flex', gap: '0.625rem', flexWrap: 'wrap', alignItems: 'center'
            }}>
              <input
                type="text"
                placeholder="Search name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)', fontSize: '0.875rem',
                  flex: 1, minWidth: '180px', maxWidth: '280px',
                  backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', outline: 'none'
                }}
              />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)', fontSize: '0.875rem',
                  cursor: 'pointer', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', outline: 'none'
                }}
              >
                <option value="">All Roles</option>
                {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'super_admin').map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)', fontSize: '0.875rem',
                  cursor: 'pointer', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', outline: 'none'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>

              {/* College filter — only for university tenants */}
              {isUniversityTenant && (
                <select
                  value={collegeFilter}
                  onChange={(e) => setCollegeFilter(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                    border: '1px solid var(--border-color)', fontSize: '0.875rem',
                    cursor: 'pointer', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', outline: 'none'
                  }}
                >
                  <option value="">All Colleges</option>
                  {colleges.map(c => (
                    <option key={c.college_id || c.id} value={c.college_id || c.id}>{c.college_name || c.name}</option>
                  ))}
                </select>
              )}

              <div style={{ flex: 1 }} />

              {selected.size > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.3rem 0.75rem', borderRadius: '0.375rem',
                  backgroundColor: 'var(--primary-light)', color: 'var(--primary)',
                  fontSize: '0.8rem', fontWeight: 600
                }}>
                  <span>{selected.size} selected</span>
                  <button
                    className="btn btn-success btn-sm"
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem' }}
                    onClick={() => handleBulkAction('activate')}
                    disabled={bulkLoading}
                  >
                    Activate
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem' }}
                    onClick={() => handleBulkAction('deactivate')}
                    disabled={bulkLoading}
                  >
                    Deactivate
                  </button>
                  <button
                    onClick={() => setSelected(new Set())}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontWeight: 700, padding: '0 0.1rem' }}
                  >
                    ✕
                  </button>
                </div>
              )}

              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowBulkUpload(true)}
                title="Bulk import users from Excel"
              >
                ↑ Bulk Upload
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleExport}
                disabled={users.length === 0}
                title="Export current view as CSV"
              >
                ↓ Export CSV
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={fetchUsers}
                title="Refresh"
                style={{ padding: '0.4rem 0.6rem' }}
              >
                ↻
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowCreateModal(true)}
              >
                + Create User
              </button>
            </div>

            {/* ── Feedback ────────────────────────────────────── */}
            {toast && (
              <div style={{
                margin: '0.75rem 1.5rem',
                padding: '0.625rem 1rem', borderRadius: '0.375rem', fontSize: '0.875rem',
                backgroundColor: toast.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)',
                color: toast.type === 'success' ? 'var(--success)' : 'var(--danger)'
              }}>
                {toast.msg}
              </div>
            )}
            {impersonateError && (
              <div style={{
                margin: '0.75rem 1.5rem',
                padding: '0.625rem 1rem', borderRadius: '0.375rem', fontSize: '0.875rem',
                backgroundColor: 'var(--danger-light)', color: 'var(--danger)'
              }}>
                {impersonateError}
              </div>
            )}

            {/* ── Table ───────────────────────────────────────── */}
            <div style={{ padding: '0 1.5rem 1rem' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>Loading users...</div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{error}</p>
                  <button className="btn btn-primary btn-sm" onClick={fetchUsers}>Retry</button>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={handleSelectAll}
                            style={{ cursor: 'pointer' }}
                            title="Select all on this page"
                          />
                        </th>
                        <th>User</th>
                        <th>Role</th>
                        <th>Scope</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th>Joined</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: 'center', padding: '3.5rem 1rem', color: 'var(--text-tertiary)' }}>
                            <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'center' }}><Icon name="Search" size={32} /></div>
                            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>No users found</div>
                            {(search || roleFilter || statusFilter) && (
                              <div style={{ fontSize: '0.875rem' }}>Try clearing your filters</div>
                            )}
                          </td>
                        </tr>
                      ) : (
                        users.map(user => {
                          const rb = ROLE_BADGE(user.role);
                          const sb = STATUS_BADGE(user.status);
                          const isToggling = togglingId === user.id;
                          const isImpersonating = impersonatingId === user.id;
                          const isConfirming = confirmImpersonateId === user.id;
                          return (
                            <tr
                              key={user.id}
                              style={{ backgroundColor: selected.has(user.id) ? 'var(--primary-light)' : undefined }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selected.has(user.id)}
                                  onChange={() => handleSelectOne(user.id)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </td>
                              <td>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>{user.name}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{user.email}</div>
                                {user.phone && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{user.phone}</div>
                                )}
                              </td>
                              <td>
                                <span style={{
                                  fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.5rem',
                                  borderRadius: '4px', backgroundColor: rb.bg, color: rb.color,
                                  whiteSpace: 'nowrap', display: 'inline-block'
                                }}>
                                  {ROLE_LABELS[user.role] || user.role}
                                </span>
                              </td>
                              <td>
                                <div style={{ fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                                  {user.college_name ? (
                                    <span style={{ color: 'var(--text-secondary)' }}>{user.college_name}</span>
                                  ) : null}
                                  {user.department_name ? (
                                    <span style={{ color: 'var(--text-tertiary)' }}>
                                      {user.department_name}
                                      {user.department_code && (
                                        <span style={{ fontWeight: 700, color: 'var(--primary)' }}> ({user.department_code})</span>
                                      )}
                                    </span>
                                  ) : null}
                                  <SubjectsCell subjects={user.subjects} />
                                  {!user.college_name && !user.department_name && (
                                    <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span style={{
                                  fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.5rem',
                                  borderRadius: '4px', backgroundColor: sb.bg, color: sb.color
                                }}>
                                  {user.status}
                                </span>
                              </td>
                              <td style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                                {formatElapsed(user.last_login)}
                              </td>
                              <td style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                  <button
                                    onClick={() => setDetailUser(user)}
                                    className="btn btn-secondary btn-sm"
                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={() => setEditUser(user)}
                                    className="btn btn-secondary btn-sm"
                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => setResetPwdUser(user)}
                                    className="btn btn-secondary btn-sm"
                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                                    title="Reset Password"
                                  >
                                    Pwd
                                  </button>
                                  <button
                                    onClick={() => handleToggleStatus(user)}
                                    disabled={isToggling}
                                    style={{
                                      padding: '0.25rem 0.5rem', borderRadius: '9999px',
                                      border: 'none', fontSize: '0.7rem', fontWeight: 600,
                                      cursor: isToggling ? 'not-allowed' : 'pointer',
                                      opacity: isToggling ? 0.6 : 1,
                                      backgroundColor: user.is_active ? 'var(--success-light)' : 'var(--bg-tertiary)',
                                      color: user.is_active ? 'var(--success)' : 'var(--text-secondary)'
                                    }}
                                  >
                                    {isToggling ? '...' : user.is_active ? 'Active' : 'Inactive'}
                                  </button>
                                  <button
                                    onClick={() => handleImpersonateClick(user.id)}
                                    disabled={isImpersonating}
                                    style={{
                                      padding: '0.25rem 0.5rem', borderRadius: '0.375rem',
                                      border: 'none', fontSize: '0.7rem', fontWeight: 600,
                                      cursor: isImpersonating ? 'not-allowed' : 'pointer',
                                      opacity: isImpersonating ? 0.6 : 1,
                                      backgroundColor: isConfirming ? '#dc2626' : '#7c3aed',
                                      color: 'white'
                                    }}
                                    title={isConfirming ? 'Click again to confirm impersonation' : 'Log in as this user'}
                                  >
                                    {isImpersonating ? '...' : isConfirming ? 'Confirm?' : 'Login As'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {!loading && !error && (
                <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
              )}
            </div>
            </>) : (
              <UploadLogsTab tenantSlug={selectedTenantSlug} />
            )}
          </>
        )}
      </div>

      {/* ── Modals ─────────────────────────────────────────────── */}
      {showCreateModal && (
        <CreateEditModal
          mode="create"
          tenantSlug={selectedTenantSlug}
          tenantType={selectedTenant?.tenant_type}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); fetchUsers(); showToast('User created successfully'); }}
        />
      )}
      {editUser && (
        <CreateEditModal
          mode="edit"
          tenantSlug={selectedTenantSlug}
          tenantType={selectedTenant?.tenant_type}
          initialData={editUser}
          onClose={() => setEditUser(null)}
          onSuccess={() => { setEditUser(null); fetchUsers(); showToast('User updated successfully'); }}
        />
      )}
      {resetPwdUser && (
        <ResetPasswordModal
          tenantSlug={selectedTenantSlug}
          user={resetPwdUser}
          onClose={() => setResetPwdUser(null)}
          onSuccess={() => { setResetPwdUser(null); showToast('Password reset successfully'); }}
        />
      )}
      {detailUser && (
        <UserDetailModal user={detailUser} onClose={() => setDetailUser(null)} />
      )}
      {showBulkUpload && (
        <BulkUploadModal
          tenantSlug={selectedTenantSlug}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => { setShowBulkUpload(false); showToast('Bulk upload complete'); setActiveMainTab('logs'); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create / Edit Modal
// ─────────────────────────────────────────────────────────────────────────────
function CreateEditModal({ mode, tenantSlug, tenantType, initialData, onClose, onSuccess }) {
  const isEdit = mode === 'edit';
  const isUniTenant = tenantType === 'university';

  const [form, setForm] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    role: initialData?.role || '',
    college_id: initialData?.college_id ? String(initialData.college_id) : '',
    department_id: initialData?.department_id ? String(initialData.department_id) : '',
    // subject_id removed from form - will be assigned after user creation
  });
  const [colleges, setColleges] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [, setSubjects] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const roleList = isUniTenant
    ? ['university_admin', 'college_admin', 'coe', 'hod', 'moderator', 'subject_matter_expert', 'examiner']
    : ['college_admin', 'coe', 'hod', 'moderator', 'subject_matter_expert', 'examiner'];

  const nc = roleNeedsCollege(form.role);
  const nd = roleNeedsDepartment(form.role);
  // Subject assignment removed from user creation

  useEffect(() => {
    if (nc && tenantSlug) {
      API.get(`/tenants/${tenantSlug}/colleges`)
        .then(({ data }) => { if (data.success) setColleges(data.colleges || []); })
        .catch(() => {});
    } else {
      setColleges([]);
    }
  }, [nc, tenantSlug]);

  useEffect(() => {
    if (nd && form.college_id && tenantSlug) {
      API.get(`/tenants/${tenantSlug}/departments`, { params: { college_id: form.college_id } })
        .then(({ data }) => setDepartments(data.departments || []))
        .catch(() => {});
    } else {
      setDepartments([]);
    }
  }, [nd, form.college_id, tenantSlug]);

  // Subject fetching removed - will be assigned after user creation

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'role') {
      setForm(f => ({ ...f, role: value, college_id: '', department_id: '' }));
      setDepartments([]);
      setSubjects([]);
    } else if (name === 'college_id') {
      setForm(f => ({ ...f, college_id: value, department_id: '', subject_id: '' }));
      setSubjects([]);
    } else if (name === 'department_id') {
      setForm(f => ({ ...f, department_id: value, subject_id: '' }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const validate = () => {
    if (!form.role) return 'Please select a role';
    if (!form.name.trim()) return 'Full name is required';
    if (!form.email.trim()) return 'Email is required';
    if (nc && !isEdit && !form.college_id) return 'Please select a college for this role';
    if (nd && !isEdit && !form.department_id) return 'Please select a department for this role';
    // Subject assignment removed from user creation validation
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone || null,
        role: form.role,
        college_id: form.college_id || null,
        department_id: form.department_id || null,
        // subject_id removed from user creation
      };
      if (isEdit) {
        const { data } = await API.put(`/tenants/${tenantSlug}/users/${initialData.id}`, payload);
        if (data.success) onSuccess(data);
      } else {
        const { data } = await API.post(`/tenants/${tenantSlug}/users`, {
          ...payload,
          email: form.email.trim(),
        });
        if (data.success) onSuccess(data);
      }
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} user`);
    } finally {
      setSubmitting(false);
    }
  };

  const roleInfo = {
    university_admin: 'University-level access. University is auto-resolved from tenant.',
    college_admin: 'College-level access. Requires college selection.',
    coe: 'Controller of Examination. University-level access.',
    hod: 'Head of Department. Requires college and department.',
    moderator: 'Subject-level access. Requires college, department, and subject.',
    subject_matter_expert: 'Subject-level access. Requires college, department, and subject.',
    examiner: 'Subject-level access. Requires college, department, and subject.',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)', borderRadius: '0.75rem',
        padding: '2rem', maxWidth: '560px', width: '90%',
        boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              {isEdit ? 'Edit User' : 'Create New User'}
            </h3>
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {isEdit ? `Editing ${initialData?.name}` : 'User is immediately active — no approval workflow'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-tertiary)', lineHeight: 1 }}>✕</button>
        </div>

        {error && (
          <div style={{
            marginBottom: '1rem', padding: '0.625rem 0.875rem', borderRadius: '0.375rem',
            fontSize: '0.875rem', backgroundColor: 'var(--danger-light)', color: 'var(--danger)'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Role — hidden in edit mode */}
          {!isEdit && (
            <>
              <div className="form-group">
                <label className="form-label">Role *</label>
                <select name="role" className="form-select" value={form.role} onChange={handleChange} required>
                  <option value="">— Select a role —</option>
                  {roleList.map(r => (
                    <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                  ))}
                </select>
              </div>

              {form.role && roleInfo[form.role] && (
                <div style={{
                  marginBottom: '1rem', padding: '0.5rem 0.875rem', borderRadius: '0.375rem',
                  backgroundColor: 'var(--bg-secondary)', fontSize: '0.8rem', color: 'var(--text-secondary)',
                  borderLeft: '3px solid var(--primary)'
                }}>
                  {roleInfo[form.role]}
                </div>
              )}
            </>
          )}

          {/* Name + Email */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input
                type="text" name="name" className="form-input"
                value={form.name} onChange={handleChange}
                placeholder="Full name" required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email" name="email" className="form-input"
                value={form.email} onChange={handleChange}
                placeholder="Email address" required
                disabled={isEdit}
                style={isEdit ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>

          {/* Phone */}
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel" name="phone" className="form-input"
              value={form.phone} onChange={handleChange}
              placeholder="Phone number (optional)"
            />
          </div>

          {!isEdit && (
            <div style={{ background: 'var(--info-light, #e0f2fe)', color: 'var(--info, #0369a1)', borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              Temporary password set to <strong>Test@123</strong>
            </div>
          )}

          {/* College — hidden in edit mode */}
          {nc && !isEdit && (
            <div className="form-group">
              <label className="form-label">College *</label>
              <select name="college_id" className="form-select" value={form.college_id} onChange={handleChange} required>
                <option value="">— Select College —</option>
                {colleges.map(c => (
                  <option key={c.college_id} value={String(c.college_id)}>{c.college_name}</option>
                ))}
              </select>
              {colleges.length === 0 && (
                <p style={{ fontSize: '0.78rem', color: 'var(--warning)', marginTop: '0.3rem' }}>
                  No colleges found in this tenant
                </p>
              )}
            </div>
          )}

          {/* Department — hidden in edit mode */}
          {nd && !isEdit && (
            <div className="form-group">
              <label className="form-label">Department *</label>
              <select
                name="department_id" className="form-select"
                value={form.department_id} onChange={handleChange}
                required disabled={!form.college_id}
              >
                <option value="">— Select Department —</option>
                {departments.map(d => (
                  <option key={d.department_id} value={String(d.department_id)}>
                    {d.department_name}{d.department_code ? ` (${d.department_code})` : ''}
                  </option>
                ))}
              </select>
              {!form.college_id && (
                <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.3rem' }}>
                  Select a college first
                </p>
              )}
              {form.college_id && departments.length === 0 && (
                <p style={{ fontSize: '0.78rem', color: 'var(--warning)', marginTop: '0.3rem' }}>
                  No departments found for this college
                </p>
              )}
            </div>
          )}

          {/* Subject field removed - will be assigned after user creation */}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary btn-sm" disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
              {submitting
                ? (isEdit ? 'Saving...' : 'Creating...')
                : (isEdit ? 'Save Changes' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reset Password Modal
// ─────────────────────────────────────────────────────────────────────────────
function ResetPasswordModal({ tenantSlug, user, onClose, onSuccess }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setError('');
    setSubmitting(true);
    try {
      const { data } = await API.post(
        `/tenants/${tenantSlug}/users/${user.id}/reset-password`,
        { new_password: newPassword }
      );
      if (data.success) onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)', borderRadius: '0.75rem',
        padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: 'var(--shadow-xl)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Reset Password
            </h3>
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              New password for <strong>{user.name}</strong>
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-tertiary)', lineHeight: 1 }}>✕</button>
        </div>

        {error && (
          <div style={{
            marginBottom: '1rem', padding: '0.625rem 0.875rem', borderRadius: '0.375rem',
            fontSize: '0.875rem', backgroundColor: 'var(--danger-light)', color: 'var(--danger)'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
              />
              <button
                type="button" className="password-toggle-btn"
                onClick={() => setShowPassword(v => !v)} tabIndex={-1}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password *</label>
            <input
              type="password" className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
              required
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p style={{ fontSize: '0.78rem', color: 'var(--danger)', marginTop: '0.25rem' }}>
                Passwords do not match
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary btn-sm" disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-danger btn-sm"
              disabled={submitting || (confirmPassword.length > 0 && newPassword !== confirmPassword)}
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// User Detail Modal
// ─────────────────────────────────────────────────────────────────────────────
function UserDetailModal({ user, onClose }) {
  const rb = ROLE_BADGE(user.role);
  const sb = STATUS_BADGE(user.status);

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)', borderRadius: '0.75rem',
        padding: '2rem', maxWidth: '480px', width: '90%',
        boxShadow: 'var(--shadow-xl)', maxHeight: '85vh', overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            User Profile
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-tertiary)', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', padding: '1rem', borderRadius: '0.5rem', backgroundColor: 'var(--bg-secondary)' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            backgroundColor: rb.bg, color: rb.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '1.2rem', flexShrink: 0
          }}>
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>{user.name}</div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{user.email}</div>
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.15rem 0.45rem', borderRadius: '4px', backgroundColor: rb.bg, color: rb.color }}>
                {ROLE_LABELS[user.role] || user.role}
              </span>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.15rem 0.45rem', borderRadius: '4px', backgroundColor: sb.bg, color: sb.color }}>
                {user.status}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          <InfoRow label="User ID" value={`#${user.id}`} />
          <InfoRow label="Phone" value={user.phone} />
          {user.college_name && <InfoRow label="College" value={user.college_name} />}
          {user.department_name && (
            <InfoRow label="Department" value={`${user.department_name}${user.department_code ? ` (${user.department_code})` : ''}`} />
          )}
          {user.subjects?.length > 0 && (
            <InfoRow label={user.subjects.length > 1 ? `Subjects (${user.subjects.length})` : 'Subject'} value={user.subjects.map(subjectLabel).join(', ')} />
          )}
          <InfoRow label="Joined" value={user.created_at ? new Date(user.created_at).toLocaleString() : null} />
          <InfoRow label="Last Login" value={user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'} />
        </div>

        <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
          <button onClick={onClose} className="btn btn-primary btn-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk Upload Modal
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Upload Logs Tab
// ─────────────────────────────────────────────────────────────────────────────
function UploadLogsTab({ tenantSlug }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const PAGE_SIZE = 20;

  const fetchLogs = useCallback(async () => {
    if (!tenantSlug) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/tenants/${tenantSlug}/upload-logs`, { params: { page, limit: PAGE_SIZE } });
      if (data.success) { setLogs(data.batches || []); setTotal(data.total || 0); }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [tenantSlug, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const BATCH_STATUS_BADGE = (s) => ({
    done: { bg: 'var(--success-light)', color: 'var(--success)' },
    importing: { bg: 'var(--warning-light)', color: 'var(--warning)' },
    failed: { bg: 'var(--danger-light)', color: 'var(--danger)' },
    preview: { bg: 'var(--info-light)', color: 'var(--info)' },
  }[s] || { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)' });

  return (
    <div style={{ padding: '1rem 1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>Upload History</div>
        <button className="btn btn-secondary btn-sm" onClick={() => fetchLogs()} title="Refresh">↻ Refresh</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>Loading logs...</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
          <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'center', color: 'var(--text-tertiary)' }}><Icon name="FileText" size={36} /></div>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>No upload logs yet</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Use the Bulk Upload button to import users</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Batch ID</th>
                <th>File</th>
                <th>Uploaded By</th>
                <th style={{ textAlign: 'center' }}>Total</th>
                <th style={{ textAlign: 'center' }}>Imported</th>
                <th style={{ textAlign: 'center' }}>Failed</th>
                <th>Status</th>
                <th>Date</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(b => {
                const bs = BATCH_STATUS_BADGE(b.status);
                return (
                  <tr key={b.batch_id}>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>#{b.batch_id}</td>
                    <td style={{ fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.filename}>
                      {b.filename}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>{b.uploaded_by_name || '—'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{b.uploaded_by_email || ''}</div>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{b.total_rows}</td>
                    <td style={{ textAlign: 'center', color: 'var(--success)', fontWeight: 600 }}>{b.imported_rows}</td>
                    <td style={{ textAlign: 'center', color: b.failed_rows > 0 ? 'var(--danger)' : 'var(--text-secondary)', fontWeight: 600 }}>{b.failed_rows}</td>
                    <td>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: '4px', backgroundColor: bs.bg, color: bs.color }}>
                        {b.status}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                        onClick={() => setSelectedBatch(b)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
      )}

      {selectedBatch && (
        <LogDetailModal tenantSlug={tenantSlug} batch={selectedBatch} onClose={() => setSelectedBatch(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Log Detail Modal
// ─────────────────────────────────────────────────────────────────────────────
function LogDetailModal({ tenantSlug, batch, onClose }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadErr, setDownloadErr] = useState('');
  const PAGE_SIZE = 50;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const { data } = await API.get(`/tenants/${tenantSlug}/upload-logs/${batch.batch_id}`, { params });
      if (data.success) { setRows(data.rows || []); setTotal(data.total || 0); }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [tenantSlug, batch.batch_id, page, statusFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [statusFilter]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleDownloadFailed = async () => {
    setDownloadErr('');
    try {
      const response = await API.get(`/tenants/${tenantSlug}/upload-logs/${batch.batch_id}/failed-rows`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `failed_rows_batch_${batch.batch_id}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setDownloadErr('No failed rows or download failed');
      setTimeout(() => setDownloadErr(''), 3000);
    }
  };

  const ROW_STATUS_BADGE = (s) => ({
    imported: { bg: 'var(--success-light)', color: 'var(--success)' },
    failed: { bg: 'var(--danger-light)', color: 'var(--danger)' },
    valid: { bg: 'var(--info-light)', color: 'var(--info)' },
  }[s] || { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)' });

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)', borderRadius: '0.75rem',
        padding: '2rem', maxWidth: '900px', width: '96%',
        boxShadow: 'var(--shadow-xl)', maxHeight: '92vh', overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Batch #{batch.batch_id} — {batch.filename}
            </h3>
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Uploaded {batch.created_at ? new Date(batch.created_at).toLocaleString() : '—'} by {batch.uploaded_by_name || 'unknown'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-tertiary)', lineHeight: 1 }}>✕</button>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
          {[
            { label: 'Total', value: batch.total_rows, color: 'var(--text-primary)' },
            { label: 'Imported', value: batch.imported_rows, color: 'var(--success)' },
            { label: 'Failed', value: batch.failed_rows, color: batch.failed_rows > 0 ? 'var(--danger)' : 'var(--text-secondary)' },
            { label: 'Status', value: batch.status, color: 'var(--info)' },
          ].map((c, i) => (
            <div key={i} style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: 'var(--bg-secondary)', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '0.4rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.875rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', cursor: 'pointer' }}
          >
            <option value="">All Rows</option>
            <option value="imported">Imported</option>
            <option value="failed">Failed</option>
          </select>
          {batch.failed_rows > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={handleDownloadFailed}>
              ↓ Download Failed Rows
            </button>
          )}
          {downloadErr && (
            <span style={{ fontSize: '0.8rem', color: 'var(--danger)' }}>{downloadErr}</span>
          )}
        </div>

        {/* Rows table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Loading rows...</div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '0.5rem' }}>
            <table className="table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 40 }}>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>College</th>
                  <th>Dept</th>
                  <th>Status</th>
                  <th style={{ minWidth: 200 }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>No rows</td>
                  </tr>
                ) : rows.map((r, i) => {
                  const bs = ROW_STATUS_BADGE(r.status);
                  return (
                    <tr key={i} style={{ backgroundColor: r.status === 'failed' ? 'var(--danger-light)' : undefined }}>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{r.row_num || r.row_number}</td>
                      <td style={{ fontSize: '0.8rem' }}>{r.full_name || '—'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{r.email || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{r.role || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{r.college_name || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{r.department_code || '—'}</td>
                      <td>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.4rem', borderRadius: '4px', backgroundColor: bs.bg, color: bs.color }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--danger)', maxWidth: 200, wordBreak: 'break-word' }}>
                        {r.error_message || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
        )}

        <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
          <button className="btn btn-primary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default SuperAdminUserManagement;
